import { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Grid2,
} from '@mui/material';
import { FeaturesList } from 'app/pages/SupportUs/SupportUsContent/FeatureList';
import { useTranslation } from 'react-i18next';
import { formatToUSDollars } from 'shared/utils';
import { useOrganizationPricing } from '../useOrganizationPricing';

type PlanType = 'regular' | 'pro';

type UseOrganizationFeatureDialog = {
  open: boolean;
  planType: PlanType;
  openDialog: (type: PlanType) => void;
  closeDialog: () => void;
  FeatureDialog: JSX.Element;
};

export const useOrganizationFeatureDialog =
  (): UseOrganizationFeatureDialog => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [planType, setPlanType] = useState<PlanType>('regular');
    const { pricing } = useOrganizationPricing();

    const openDialog = useCallback((type: PlanType): void => {
      setPlanType(type);
      setOpen(true);
    }, []);

    const closeDialog = useCallback((): void => {
      setOpen(false);
    }, []);

    const FeatureDialog = (
      <Dialog open={open} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>
          <Typography variant="h2">
            {t(`organizationPurchase.features.${planType}Title`)}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="h3" sx={{ fontWeight: 600, marginBottom: 1 }}>
            {t(`organizationPurchase.features.keyFeatures`)}
          </Typography>
          <FeaturesList
            plan={planType === 'regular' ? 'yearly' : 'pro_yearly'}
          />
          <Typography variant="h3" sx={{ fontWeight: 600, marginTop: 2 }}>
            {t(`organizationPurchase.features.pricingAndDiscounts`)}
          </Typography>
          <Grid2 container sx={{ marginTop: 1 }} justifyContent="space-between">
            <Typography sx={{ fontWeight: 'bold' }}>
              {t('organizationPurchase.features.memberships')}
            </Typography>
            <Typography sx={{ fontWeight: 'bold' }}>
              {t('organizationPurchase.features.perPerson')}
            </Typography>
          </Grid2>
          {pricing?.seats[planType].map((tier) => (
            <Grid2
              container
              key={tier.startingQuantity}
              sx={{ paddingTop: 1 }}
              justifyContent="space-between"
            >
              <Typography>
                {tier.endingQuantity === 999999999
                  ? `${tier.startingQuantity}+`
                  : `${tier.startingQuantity} - ${tier.endingQuantity}`}
              </Typography>
              <Typography>{formatToUSDollars(tier.unitAmount, 2)}</Typography>
            </Grid2>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} color="inherit" fullWidth size="large">
            {t('common.close')}
          </Button>
        </DialogActions>
      </Dialog>
    );

    return { open, planType, openDialog, closeDialog, FeatureDialog };
  };
